/**
 * Domain hook for Drills module - Anchor Mode v2.0 compliant
 * 
 * Base key: 'drills' (vessel scoping handled by useStorageState)
 * Single responsibility: Manage drill completion records
 * RBAC-gated mutations
 * One write per save
 */

import { useMemo, useCallback } from 'react';
import { useStorageState } from '../../useStorageState';
import { usePermissions } from '../../usePermissions';
import { filterTombstoned } from '../../../sync/tombstone';  // FT-458: hide soft-deleted drills from list

// RBAC multi-signature shim
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch { 
    return false; 
  }
};

// Deterministic ID generator
const makeId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `DRL-${timestamp}-${random}`;
};

/**
 * Normalize legacy drill records to canonical format
 * Accepts various legacy field names and converts to standard shape
 */
const normalizeLegacy = (rawList) => {
  const arr = Array.isArray(rawList) ? rawList : [];
  return arr.map((r) => {
    // Handle various date field names
    const completedAt =
      typeof r.completedAt === 'number'
        ? r.completedAt
        : r.date_completed
          ? Date.parse(r.date_completed)
          : r.dateCompleted
            ? Date.parse(r.dateCompleted)
            : r.date
              ? Date.parse(r.date)
              : null;

    // Normalize status (canonical: 'completed' or 'failed')
    const statusRaw = (r.status || r.State || r.outcome || '').toString().toLowerCase();
    const status = statusRaw === 'failed' ? 'failed' : 'completed';

    // Handle drill type variations
    const type = (r.type || r.drillType || r.title || '').toString().trim();

    return {
      id: r.id || makeId(),
      type,
      notes: r.notes || r.comment || r.comments || '',
      performedBy: r.performedBy || r.inspector || r.reported_by || r.user || 'Unknown',
      completedAt: Number.isFinite(completedAt) ? completedAt : Date.now(),
      status,
      // Preserve additional fields that might exist
      participants: r.participants || [],
      duration: r.duration || null,
      location: r.location || ''
    };
  });
};

/**
 * Drills domain hook - Anchor Mode v2.0 compliant
 * 
 * @returns {Object} Drill management API
 * @returns {Array} list - Normalized list of drill records
 * @returns {Function} addCompletion - Add a new drill completion (RBAC-gated)
 * @returns {Function} updateDrill - Update existing drill (RBAC-gated)
 * @returns {Function} deleteDrill - Delete a drill record (RBAC-gated)
 */
export const useDrills = () => {
  // Base key only - vessel scoping handled by useStorageState
  const { value: raw = [], setValue } = useStorageState('drills', []);
  const { can } = usePermissions();

  // Normalize on read
  // FT-458: exclude soft-deleted (tombstoned) drills from the display list. Raw
  // (incl. tombstones) is kept for mutations via setValue(prev=>...).
  const list = useMemo(() => filterTombstoned(normalizeLegacy(raw)), [raw]);

  /**
   * Add a new drill completion record
   * RBAC-gated: requires drills:create permission
   */
  const addCompletion = useCallback((input = {}) => {
    if (!canDo(can, 'drills', 'create')) {

      return null;
    }

    const now = Date.now();
    const record = {
      id: makeId(),
      type: (input.type || '').trim(),
      notes: input.notes || '',
      performedBy: input.performedBy || input.user || input.inspector || 'Unknown',
      completedAt: input.completedAt || now,
      status: input.status || 'completed',
      participants: input.participants || [],
      duration: input.duration || null,
      location: input.location || ''
    };

    // ONE write per save
    setValue(prev => {
      const current = normalizeLegacy(prev);
      return [...current, record];
    });

    return record.id;
  }, [can, setValue]);

  /**
   * Update an existing drill record
   * RBAC-gated: requires drills:update permission
   */
  const updateDrill = useCallback((id, updates = {}) => {
    if (!canDo(can, 'drills', 'update')) {

      return false;
    }

    // ONE write per save
    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(drill => 
        drill.id === id 
          ? { ...drill, ...updates, id } // Preserve ID
          : drill
      );
    });

    return true;
  }, [can, setValue]);

  /**
   * Delete a drill record
   * RBAC-gated: requires drills:delete permission
   */
  const deleteDrill = useCallback((id) => {
    if (!canDo(can, 'drills', 'delete')) {

      return false;
    }

    // FT-458: soft-delete via tombstone (stamp deleted_at + KEEP in array) so the
    // deletion propagates through the cloud∪local sync union instead of the
    // still-present cloud copy resurrecting a hard-removed item. Mirrors the live
    // DrillsModule delete + shipped FT-175 crew. (This hook's deleteDrill is not
    // currently wired to any UI, but is fixed defensively so a future wiring
    // can't reintroduce the resurrection bug.)
    const nowIso = new Date().toISOString();
    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(drill =>
        drill.id === id
          ? { ...drill, deleted_at: nowIso, updatedAt: Date.now() }
          : drill
      );
    });

    return true;
  }, [can, setValue]);

  return { 
    list, 
    addCompletion,
    updateDrill,
    deleteDrill
  };
};

export default useDrills;